/**
 * @module events
 * @description Public pub/sub and webhook adapter contract surface. Runtime
 * event implementations live in provider packages; this subpath exports only
 * the interface, Zod schemas, and thin error class.
 */

import type {
  AdapterHealth as SharedAdapterHealth,
  AdapterListOptions as SharedAdapterListOptions,
  EventEnvelope as SharedEventEnvelope,
  EventPublishInput as SharedEventPublishInput,
  EventSubscribeInput as SharedEventSubscribeInput,
  EventSubscription as SharedEventSubscription,
  EventsAdapter as SharedEventsAdapter,
  WebhookDelivery as SharedWebhookDelivery,
  WebhookEmitInput as SharedWebhookEmitInput,
} from "@geenius/adapters";
import {
  AdapterHealthSchema as SharedAdapterHealthSchema,
  AdapterListOptionsSchema as SharedAdapterListOptionsSchema,
  EventEnvelopeSchema as SharedEventEnvelopeSchema,
  EventPublishInputSchema as SharedEventPublishInputSchema,
  EventSubscribeInputSchema as SharedEventSubscribeInputSchema,
  EventsContractError as SharedEventsContractError,
  WebhookDeliverySchema as SharedWebhookDeliverySchema,
  WebhookEmitInputSchema as SharedWebhookEmitInputSchema,
} from "@geenius/adapters";

/**
 * Health result returned by event providers after checking pub/sub and webhook
 * connectivity.
 *
 * @example
 * ```ts
 * const health: AdapterHealth = await events.getHealth();
 * ```
 */
export type AdapterHealth = SharedAdapterHealth;

/**
 * Pagination and topic-prefix options accepted by `EventsAdapter.listEvents()`.
 *
 * @example
 * ```ts
 * const options: AdapterListOptions = { limit: 25, prefix: "user." };
 * ```
 */
export type AdapterListOptions = SharedAdapterListOptions;

/**
 * Serializable event envelope returned by publish, list, and subscription
 * handlers.
 *
 * @example
 * ```ts
 * const event: EventEnvelope = { id: "evt_1", topic: "user.created", payload: {}, metadata: {}, createdAt: new Date().toISOString() };
 * ```
 */
export type EventEnvelope = SharedEventEnvelope;

/**
 * Provider-neutral publish payload accepted by events adapters.
 *
 * @example
 * ```ts
 * const input: EventPublishInput = { topic: "user.created", payload: { id: "u1" } };
 * ```
 */
export type EventPublishInput = SharedEventPublishInput;

/**
 * Provider-neutral subscribe payload accepted before binding a topic handler.
 *
 * @example
 * ```ts
 * const input: EventSubscribeInput = { topic: "user.created" };
 * ```
 */
export type EventSubscribeInput = SharedEventSubscribeInput;

/**
 * Unsubscribe handle returned by `EventsAdapter.subscribe()`.
 *
 * @example
 * ```ts
 * await subscription.unsubscribe();
 * ```
 */
export type EventSubscription = SharedEventSubscription;

/**
 * Serializable webhook delivery record returned after emitting an event.
 *
 * @example
 * ```ts
 * const delivery: WebhookDelivery = { id: "del_1", url: "https://example.com", eventId: "evt_1", status: "queued", createdAt: new Date().toISOString() };
 * ```
 */
export type WebhookDelivery = SharedWebhookDelivery;

/**
 * Provider-neutral webhook emit payload accepted before invoking a concrete
 * delivery transport.
 *
 * @example
 * ```ts
 * const input: WebhookEmitInput = { url: "https://example.com", event };
 * ```
 */
export type WebhookEmitInput = SharedWebhookEmitInput;

/**
 * SDK-free pub/sub and webhook adapter contract implemented by downstream
 * provider packages and app-owned event modules.
 *
 * @example
 * ```ts
 * const event = await events.publish({ topic: "user.created" });
 * ```
 */
export type EventsAdapter = SharedEventsAdapter;

/**
 * Runtime validator for the common events adapter health response.
 *
 * @example
 * ```ts
 * const health = AdapterHealthSchema.parse({ ok: true, checkedAt: new Date().toISOString() });
 * ```
 */
export const AdapterHealthSchema = SharedAdapterHealthSchema;

/**
 * Runtime validator for event list pagination and topic-prefix filters.
 *
 * @example
 * ```ts
 * const options = AdapterListOptionsSchema.parse({ limit: 10, prefix: "user." });
 * ```
 */
export const AdapterListOptionsSchema = SharedAdapterListOptionsSchema;

/**
 * Runtime validator for serialized event envelopes returned by providers.
 *
 * @example
 * ```ts
 * const event = EventEnvelopeSchema.parse({ id: "evt_1", topic: "user.created", payload: {}, createdAt: new Date().toISOString() });
 * ```
 */
export const EventEnvelopeSchema = SharedEventEnvelopeSchema;

/**
 * Runtime validator for provider-neutral event publish payloads.
 *
 * @example
 * ```ts
 * const input = EventPublishInputSchema.parse({ topic: "user.created", payload: { id: "u1" } });
 * ```
 */
export const EventPublishInputSchema = SharedEventPublishInputSchema;

/**
 * Runtime validator for provider-neutral event subscription payloads.
 *
 * @example
 * ```ts
 * const input = EventSubscribeInputSchema.parse({ topic: "user.created" });
 * ```
 */
export const EventSubscribeInputSchema = SharedEventSubscribeInputSchema;

/**
 * Contract-layer error for invalid event payloads or provider-boundary
 * violations before a concrete SDK receives the request.
 *
 * @example
 * ```ts
 * throw new EventsContractError("Event topic is required");
 * ```
 */
export const EventsContractError = SharedEventsContractError;

/**
 * Runtime validator for provider-neutral webhook emit payloads.
 *
 * @example
 * ```ts
 * const input = WebhookEmitInputSchema.parse({ url: "https://example.com", event });
 * ```
 */
export const WebhookEmitInputSchema = SharedWebhookEmitInputSchema;

/**
 * Runtime validator for webhook delivery records returned by providers.
 *
 * @example
 * ```ts
 * const delivery = WebhookDeliverySchema.parse({ id: "del_1", url: "https://example.com", eventId: "evt_1", status: "queued", createdAt: new Date().toISOString() });
 * ```
 */
export const WebhookDeliverySchema = SharedWebhookDeliverySchema;
